import { Controller, useForm } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Dialog, DialogTitle, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, Radio, RadioGroup } from "@mui/material";
import FormField from "../../../components/form-controls/form-field";
import theme from "../../../app/app-theme";
import FormCloseButton from "../../../components/form-controls/form-close-button";
import { FormTextField } from "../../../components/form-input-field-wrappers/form-controller-inputs/form-text-field";
import { BiometricDeviceUser, BiometricDeviceUserRequest } from "../../../service/model/biometric.model";
import { UserType } from "../../../shared/enums";
import { AutocompleteInput } from "../../../components/form-input-field-wrappers/autocomplete-dropdown-input";
import { selectMembers } from "../../members/member.slice";
import { useAppSelector } from "../../../app/hooks";
import { selectUsers } from "../../user/user.slice";

interface BiometricUserFormProps {
    readonly deviceUser?: BiometricDeviceUser
    readonly editable: boolean
    readonly onClose: () => void
    readonly onSubmit: (data: BiometricDeviceUserRequest) => void
}

const style = {
    // position: 'absolute' as 'absolute',
    // top: '50%',
    // left: '50%',
    // transform: 'translate(-50%, -50%)',
    // width: 400,
    // bgcolor: 'background.paper',
    // border: '2px solid #000',
    // boxShadow: 24,
    p: 2,
};

const BiometricUserForm: React.FC<BiometricUserFormProps> = ({ deviceUser, editable, onClose, onSubmit }) => {
    const members = useAppSelector(selectMembers);
    const staff = useAppSelector(selectUsers);
    const hasMemberId = deviceUser?.memberId !== undefined && deviceUser?.memberId !== null;
    const hasBusinessUserId = deviceUser?.businessUserId !== undefined && deviceUser?.businessUserId !== null;
    let defaultUserType: UserType | undefined;
    if (hasMemberId) {
        defaultUserType = UserType.MEMBER;
    } else if (hasBusinessUserId) {
        defaultUserType = UserType.BUSINESS_USER;
    }

    const defaultUserId = deviceUser?.memberId ?? deviceUser?.businessUserId;

    const schema = yup.object().shape({
        name: yup.string()
            .required("Name is required"),
        devicePin: yup.string()
            .required("Device PIN is required"),
        userType: yup.mixed<UserType>(),
        userId: yup.number().required("User is required").typeError("User is required"),
    });

    const { register, handleSubmit, control, setValue, watch, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            name: deviceUser?.name ?? "",
            devicePin: deviceUser?.devicePin ?? "",
            userType: defaultUserType,
            userId: defaultUserId,
        }
    });

    const onSubmitHandler = (data: any) => {
        if (deviceUser) {
            const request: BiometricDeviceUserRequest = {
                ...deviceUser,
                memberId: data.userType === UserType.MEMBER ? data.userId : undefined,
                businessUserId: data.userType === UserType.BUSINESS_USER ? data.userId : undefined,
            }
            onSubmit(request)
        }
    };

    const selectedUserType = watch("userType");

    const getUserList = () => {
        switch (selectedUserType) {
            case UserType.MEMBER:
                return members;
            case UserType.BUSINESS_USER:
                return staff; // Replace with actual staff list
            default:
                return [];
        }
    };

    return (
        <Dialog open={true} onClose={onClose} sx={style} fullWidth maxWidth="xs">
            <DialogTitle>Biometric User Details</DialogTitle>
            <Box sx={style}>
                <>
                    <FormCloseButton onClose={onClose} />
                    <form onSubmit={handleSubmit(onSubmitHandler)}>
                        <FormField>
                            <FormTextField
                                id="name"
                                label="Name"
                                name="name"
                                variant="outlined"
                                defaultValue={deviceUser?.name}
                                disabled
                                error={errors.name !== undefined}
                                helperText={typeof errors.name?.message === "string" ? errors.name.message : undefined}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <FormTextField
                                id="device-pin"
                                label="Device PIN"
                                name="devicePin"
                                variant="outlined"
                                defaultValue={deviceUser?.devicePin}
                                disabled
                                error={errors.devicePin !== undefined}
                                helperText={typeof errors.devicePin?.message === "string" ? errors.devicePin.message : undefined}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <Controller
                                name="userType"
                                control={control}
                                rules={{ required: "User type is required" }}
                                defaultValue={defaultUserType}
                                render={({ field, fieldState }) => (
                                    <FormControl>
                                        <FormLabel>Select user type</FormLabel>
                                        <RadioGroup {...field} row>
                                            <FormControlLabel
                                                value={UserType.MEMBER}
                                                control={<Radio />}
                                                onClick={() => {
                                                    setValue("userType", UserType.MEMBER);
                                                    setValue("userId", Number.NaN);
                                                }}
                                                label="Member"
                                            />
                                            <FormControlLabel
                                                value={UserType.BUSINESS_USER}
                                                control={<Radio />}
                                                onClick={() => {
                                                    setValue("userType", UserType.BUSINESS_USER);
                                                    setValue("userId", Number.NaN);
                                                }}
                                                label="Staff"
                                            />
                                        </RadioGroup>

                                        <FormHelperText sx={{ color: theme.palette.error.main }}>
                                            {fieldState.error
                                                ? fieldState.error.message
                                                : ""}
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </FormField>
                        <FormField>
                            <AutocompleteInput
                                id="userId"
                                name="userId"
                                control={control}
                                defaultValue={defaultUserId}
                                disabled={!selectedUserType}
                                label="Select user"
                                options={getUserList()?.map(m => ({ id: m.id, label: `${m.firstName} ${m.lastName}` })) ?? []}
                            />
                        </FormField>
                        <Grid container direction="row" justifyContent='end' sx={{ mt: 1 }}>
                            <Button type="button" variant="contained" sx={{ mr: 1, bgcolor: `${theme.palette.secondary.main}` }} onClick={onClose}>Cancel</Button>
                            <Button type="submit" variant="contained" >Submit</Button>
                        </Grid>
                    </form>
                </>
            </Box>
        </Dialog>
    );
}

export default BiometricUserForm;